// ResolveCard — purpose-built row for the Resolve phase. Compact,
// readable, autofills from canonical metadata, hides the raw OFF /
// scan-review behind a barcode icon so the default view is JUST the
// stuff a user needs to confirm or fix.
//
// Layout (top → bottom):
//   1. Tier badge ("RESOLVED · paired → Nutmeg") in readable colors.
//      Item names render in new-kansas italic to match item-card
//      typography elsewhere in the app.
//   2. Big italic title — the canonical's display name (or quick-name
//      for yellow rows without a canonical bind).
//   3. Brand input — single inline field, commits on blur.
//   4. Single chip row, in this order:
//        package-size → location → tile → state → cut
//      Tap any chip to edit. Auto-fills location/tile/category from
//      canonical metadata when canonical changes.
//   5. Barcode icon button — toggles a small "What we know about this
//      UPC" panel showing the raw OFF data + classifier sources.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  findHub,
  findIngredient,
  fuzzyMatchIngredient,
  getCanonicalHome,
  getCanonicalTile,
} from "../../data/ingredients";
import { useIngredientInfo } from "../../lib/useIngredientInfo";
import { isGenericOnly } from "../../experiments/mcm-cooking/additem/genericCanonicals";
import ChipModal from "../../experiments/mcm-cooking/additem/ChipModal";
import RisingResults from "../../experiments/mcm-cooking/additem/RisingResults";
import PackageSizeStep from "../../experiments/mcm-cooking/additem/PackageSizeStep";
import AddItemSheet from "../../experiments/mcm-cooking/additem/AddItemSheet";
import { tilesForIngredient } from "../../lib/tileMatching";
import { countUnitInfo } from "../../lib/countUnitInfo";
import { tripActions, useShopTrip } from "../../lib/useShopTrip";
import { brandsForCanonical } from "../../data/brandExpertise";
import { useActiveStore } from "../../lib/useActiveStore";
import { useShoppingRegion } from "../../lib/useShoppingRegion";
import { TIER_COLORS } from "./tierColors";
import { COLOR, FONT, RADIUS, CHIP_TONES } from "../../lib/tokens";
import { createPendingCanonicalFromScan } from "../../lib/createPendingCanonicalFromScan";
import { readinessFor } from "../../lib/canonicalReadiness";
import { enrichIngredient } from "../../lib/enrichIngredient";

const LOCATION_OPTIONS = [
  { id: "fridge",  label: "Fridge" },
  { id: "pantry",  label: "Pantry" },
  { id: "freezer", label: "Freezer" },
];


// Small helper — ingredient → list of candidate tile ids; resolves
// emoji + label per tile via the registries used elsewhere.
import { PANTRY_TILES, pantryTileIdForItem } from "../../lib/pantryTiles";
import { FRIDGE_TILES, tileIdForItem as fridgeTileIdForItem } from "../../lib/fridgeTiles";
import { FREEZER_TILES, freezerTileIdForItem } from "../../lib/freezerTiles";
import { hubForIngredient } from "../../data/ingredients";

function tileMetaById(id) {
  if (!id) return null;
  const found =
    PANTRY_TILES.find((t) => t.id === id) ||
    FRIDGE_TILES.find((t) => t.id === id) ||
    FREEZER_TILES.find((t) => t.id === id) ||
    null;
  if (!found) return null;
  return { id: found.id, label: found.label || found.id, emoji: found.emoji };
}

function tileOptionsForCanonical(canonicalId, location) {
  // Show all tiles within the picked (or inferred) location, with the
  // canonical's recommended tile floated to the top. The PANTRY/FRIDGE/
  // FREEZER registries store the human label as `.label` (not `.name`)
  // and an emoji on `.emoji` — render both.
  const recommended = new Set(tilesForIngredient(canonicalId));
  const pool =
    location === "fridge"  ? FRIDGE_TILES :
    location === "freezer" ? FREEZER_TILES :
    location === "pantry"  ? PANTRY_TILES :
    [...PANTRY_TILES, ...FRIDGE_TILES, ...FREEZER_TILES];
  const sorted = [...pool].sort((a, b) => {
    const aRec = recommended.has(a.id) ? 0 : 1;
    const bRec = recommended.has(b.id) ? 0 : 1;
    return aRec - bRec;
  });
  return sorted.map((t) => ({
    id: t.id,
    label: t.label || t.id,
    emoji: t.emoji,
  }));
}

const cardStyle = (tone) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  background: COLOR.ground,
  border: `1px solid ${tone.border}`,
  borderLeft: `4px solid ${tone.strip}`,
  borderRadius: RADIUS.md,
  padding: "12px 14px",
  gap: 10,
});

const tierBadgeRow = (tone) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontFamily: FONT.mono,
  fontSize: 10,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  // Use ink color for the row, with the tier dot carrying tier-color.
  color: COLOR.muted,
});

const tierDot = (tone) => ({
  display: "inline-block",
  width: 8,
  height: 8,
  borderRadius: "50%",
  background: tone.strip,
  flex: "0 0 auto",
});

const pairedItemNameStyle = {
  // Item names in new-kansas italic per CLAUDE.md identity-typography.
  fontFamily: FONT.itemTitle,
  fontStyle: "italic",
  fontSize: 13,
  letterSpacing: 0,
  textTransform: "none",
  color: COLOR.ink,
};

const titleStyle = {
  fontFamily: FONT.itemTitle,
  fontStyle: "italic",
  fontWeight: 500,
  fontSize: 22,
  color: COLOR.ink,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  margin: 0,
};

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  fontFamily: FONT.forma,
  fontSize: 14,
  color: COLOR.ink,
  background: COLOR.night,
  border: `1px solid ${COLOR.edge}`,
  borderRadius: RADIUS.md,
  outline: "none",
};

const chipRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
};

const chipBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontFamily: FONT.mono,
  fontSize: 10,
  padding: "5px 9px",
  borderRadius: RADIUS.sm,
  letterSpacing: "0.06em",
  cursor: "pointer",
  border: "1px solid",
  textTransform: "uppercase",
};

// Binary chip semantics (per user 2026-05-04):
//   GREEN  — value is set / paired
//   RED    — axis is needed for full resolution but unset
// (Axes that don't apply to the canonical aren't rendered at all.)
const chipPaired = {
  ...chipBase,
  color: COLOR.leaf,
  background: COLOR.leafDeep,
  borderColor: COLOR.leafDim,
  fontWeight: 600,
};

const chipNeeded = {
  ...chipBase,
  color: COLOR.rose,
  background: COLOR.roseDeep,
  borderColor: COLOR.roseDim,
  borderWidth: 1.5,
  fontWeight: 600,
};

const iconBtnBase = {
  width: 28,
  height: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: `1px solid ${COLOR.edge}`,
  borderRadius: RADIUS.sm,
  cursor: "pointer",
  padding: 0,
};

const iconRowStyle = {
  position: "absolute",
  top: 10,
  right: 10,
  display: "flex",
  gap: 6,
};

const knownPanelStyle = {
  fontFamily: FONT.mono,
  fontSize: 10,
  color: COLOR.muted,
  background: COLOR.night,
  border: `1px solid ${COLOR.edge}`,
  borderRadius: RADIUS.md,
  padding: "8px 10px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  letterSpacing: 0,
};

export default function ResolveCard({ scan, listItem }) {
  const overrides = useShopTrip(
    (s) => s.placementOverrides.get(scan.id) || EMPTY_OVERRIDES
  );
  const userId = useShopTrip((s) => s.userId);

  const ing = scan.canonicalId ? findIngredient(scan.canonicalId) : null;
  const off = scan.offPayload || null;
  // Pull DB ingredient_info so the canonical-home cascade picks up
  // admin-approved overrides for this canonical (e.g. a Haiku-suggested
  // tile_routing.<location> override or an admin-curated storage block).
  // refreshDb is the manual cache-bust the enrich-on-tap path triggers
  // after the edge function writes — without it dbMap stays stale for
  // up to 24h (the TTL) and the readiness chips never flip.
  const { getInfo, refreshDb } = useIngredientInfo() || {};
  const dbInfo = scan.canonicalId && typeof getInfo === "function"
    ? getInfo(scan.canonicalId) : null;

  // Auto-fill location/tile from canonical metadata once. After the
  // first canonical resolution, pin defaults so the user sees them
  // immediately. User edits override and persist via setPlacementOverride.
  //
  // The home cascade (DB info → bundled INGREDIENT_INFO → hub chain)
  // lives in `getCanonicalHome`. Reading through it here means
  // arborio_rice inherits `pantry · pasta_grains` from rice_hub
  // without us re-implementing the walk per surface.
  useEffect(() => {
    if (!ing) return;
    const cur = overrides;
    const patch = {};
    const home = getCanonicalHome(scan.canonicalId, dbInfo);
    const resolvedLoc =
      cur.location ||
      home?.location ||
      defaultLocationForCategory(ing?.category) ||
      null;
    if (!cur.location && resolvedLoc) patch.location = resolvedLoc;
    if (!cur.tile_id && !cur.tileId && resolvedLoc) {
      // Tile resolution order:
      //   1. Hub-chain declarative tile for THIS location.
      //   2. Location-scoped classifier (state-aware id sets, etc.)
      // The classifier fallback covers items that don't have a hub
      // (standalone canonicals like "salt" or free-text rows) — for
      // hub-rooted canonicals like arborio_rice, step 1 wins.
      let tile = getCanonicalTile(scan.canonicalId, resolvedLoc, dbInfo);
      if (!tile) {
        const fake = { ingredientId: scan.canonicalId, category: ing?.category };
        const helpers = { findIngredient, hubForIngredient, getInfo };
        if (resolvedLoc === "pantry")  tile = pantryTileIdForItem(fake, helpers);
        else if (resolvedLoc === "freezer") tile = freezerTileIdForItem(fake, helpers);
        else if (resolvedLoc === "fridge")  tile = fridgeTileIdForItem(fake, helpers);
      }
      if (tile) patch.tileId = tile;
    }
    if (Object.keys(patch).length > 0) {
      tripActions.setPlacementOverride(scan.id, patch);
    }
  }, [scan.canonicalId, dbInfo]);  // ing/overrides intentionally excluded

  // Effective values — overrides first, then derived from canonical.
  // Override accepts both snake_case (from the wizard's pantryRow
  // payload) and camelCase (legacy autofill writes), so we look both
  // up for each axis.
  const home = ing ? getCanonicalHome(scan.canonicalId, dbInfo) : null;
  const location =
    overrides.location ||
    home?.location ||
    defaultLocationForCategory(ing?.category) ||
    null;
  const tileId = (() => {
    if (overrides.tile_id) return overrides.tile_id;
    if (overrides.tileId)  return overrides.tileId;
    if (!ing || !location) return null;
    // Declarative hub-chain tile wins over classifier inference.
    const fromHome = getCanonicalTile(scan.canonicalId, location, dbInfo);
    if (fromHome) return fromHome;
    const fake = { ingredientId: scan.canonicalId, category: ing?.category };
    const helpers = { findIngredient, hubForIngredient, getInfo };
    if (location === "pantry")  return pantryTileIdForItem(fake, helpers) || null;
    if (location === "freezer") return freezerTileIdForItem(fake, helpers) || null;
    if (location === "fridge")  return fridgeTileIdForItem(fake, helpers) || null;
    return null;
  })();
  const tileMeta = tileMetaById(tileId);
  const stateValue = overrides.state || null;
  const cutValue = overrides.cut || null;
  const packageAmount =
    overrides.max ??
    overrides.packageAmount ??
    parsedPkg(off)?.amount ??
    null;
  const packageUnit =
    overrides.unit ||
    overrides.packageUnit ||
    parsedPkg(off)?.unit ||
    null;

  // ── Picker state ──────────────────────────────────────────────────
  // Count-default canonicals (lamb chops, eggs) that still have no
  // package size mount with the size axis already open — "how many
  // chops?" is the first question, and PackageSizeStep renders its
  // count stepper for these. Lazy init: evaluated once on mount.
  const [openAxis, setOpenAxis] = useState(() =>
    countUnitInfo(scan.canonicalId).isCountDefault && !(packageAmount && packageUnit)
      ? "size"
      : null
  );
  const [canonicalSearch, setCanonicalSearch] = useState("");
  const [brandDraft, setBrandDraft] = useState(scan.brand || "");
  const [knownOpen, setKnownOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const canonicalInputRef = useRef(null);
  const brandInputRef = useRef(null);
  // Track the value our debounce last wrote so we can tell our OWN
  // round-trip apart from an external update (realtime, wizard
  // onSubmit, etc.). Without this, every debounced write echoed
  // back through `scan.brand` and the resync effect below stomped
  // on characters the user had typed in the 350ms gap — felt like
  // the keyboard was randomly deleting letters mid-word.
  const lastWroteBrandRef = useRef(scan.brand || "");

  // Sync brand into the input on row swap (different scan.id) or on
  // a TRULY external update (someone else / another tab changed it).
  // We deliberately ignore the echo of our own debounced write —
  // that path bumps lastWroteBrandRef to match scan.brand, so this
  // effect short-circuits.
  useEffect(() => {
    const incoming = scan.brand || "";
    if (incoming === lastWroteBrandRef.current) return;
    setBrandDraft(incoming);
    lastWroteBrandRef.current = incoming;
  }, [scan.brand, scan.id]);

  // Auto-commit brand on debounce so the tier flips green without
  // requiring a blur. 350ms gives the user time to finish typing.
  useEffect(() => {
    const v = brandDraft.trim();
    const cur = scan.brand || "";
    if (v === cur) return;
    const t = setTimeout(() => {
      lastWroteBrandRef.current = v;  // mark this value as "ours"
      tripActions.applyScanPatch(scan.id, { brand: v || null });
    }, 350);
    return () => clearTimeout(t);
  }, [brandDraft, scan.brand, scan.id]);

  const canonicalSearchItems = useMemo(() => {
    if (!canonicalSearch || canonicalSearch.length < 2) return [];
    return fuzzyMatchIngredient(canonicalSearch, 16)
      .filter((m) => !isGenericOnly(m.ingredient.id))
      .slice(0, 12)
      .map((m) => ({
        id: m.ingredient.id,
        label: m.ingredient.name,
        emoji: m.ingredient.emoji,
      }));
  }, [canonicalSearch]);

  function pickCanonical(item) {
    const targetIng = findIngredient(item.id);
    console.info("[resolve.pickCanonical]", {
      scanId: scan.id,
      pickedId: item.id,
      pickedLabel: item.label,
      writingPatch: {
        canonical_id: item.id,
        product_name: targetIng?.name || item.label,
      },
      currentCanonicalId: scan.canonicalId,
      currentProductName: scan.productName,
    });
    tripActions.applyScanPatch(scan.id, {
      canonical_id: item.id,
      product_name: targetIng?.name || item.label,
    });
    // Reset overrides so canonical metadata can re-derive.
    tripActions.setPlacementOverride(scan.id, {
      location: null,
      tileId: null,
    });
    setCanonicalSearch("");
    setOpenAxis(null);
  }

  // When the inline search returns no matches, the user can mint a
  // new pending canonical from the typed text. Routes through the
  // same createPendingCanonicalFromScan path that MemoryBookCapture
  // uses — slug derives from the typed name, lands in
  // pending_ingredient_info for admin review, scan binds to the
  // synthetic id immediately so the user can keep going.
  const [creatingCanonical, setCreatingCanonical] = useState(false);
  async function createCanonicalFromSearch() {
    const typed = canonicalSearch.trim();
    if (!typed || typed.length < 3 || !userId) return;
    setCreatingCanonical(true);
    try {
      const result = await createPendingCanonicalFromScan({
        userId,
        productName: typed,
        brand: scan.brand || null,
        categoryHints: off?.categoryHints || null,
      });
      if (result?.canonicalId) {
        tripActions.applyScanPatch(scan.id, {
          canonical_id: result.canonicalId,
          product_name: typed,
        });
        tripActions.setPlacementOverride(scan.id, {
          location: null,
          tileId: null,
        });
      }
    } catch (e) {
      console.warn("[ResolveCard] create canonical failed:", e?.message);
    } finally {
      setCreatingCanonical(false);
      setCanonicalSearch("");
      setOpenAxis(null);
    }
  }

  // Readiness — does the bound canonical have the nutrition + density
  // data downstream surfaces (calorie rollups, unit conversion, recipe
  // matching) need? Computed on every render; the underlying reads are
  // cheap (memoized inside getCanonicalHome / getInfo).
  const readiness = readinessFor(scan.canonicalId, getInfo);

  // On-tap enrichment for canonicals missing nutrition / density. Fires
  // the enrich-ingredient edge function (paid Haiku) — opt-in only per
  // CLAUDE.md "Paid AI features must be opt-in." Realtime broadcasts
  // the result back through useIngredientInfo so the chips flip green
  // on their own without us refetching here.
  const [enriching, setEnriching] = useState(false);
  async function enrichOnDemand() {
    if (!scan.canonicalId || enriching) return;
    setEnriching(true);
    try {
      await enrichIngredient({ canonical_id: scan.canonicalId });
      // The edge fn wrote to ingredient_info, but useIngredientInfo's
      // dbMap is TTL-cached (24h) — without an explicit refresh the
      // chips would stay rose until next boot. refreshDb re-fetches
      // the table and re-broadcasts to every subscriber.
      if (typeof refreshDb === "function") await refreshDb();
    } catch (e) {
      console.warn("[ResolveCard] enrich failed:", e?.message);
    } finally {
      setEnriching(false);
    }
  }

  function commitBrand() {
    const v = brandDraft.trim();
    lastWroteBrandRef.current = v;  // mark our own write so the resync effect ignores the echo
    tripActions.applyScanPatch(scan.id, { brand: v || null });
  }

  // Brand suggestion pills — this input was the only brand surface
  // in the app with NO suggestions, which is exactly where the
  // store-aware boost matters most (mid-trip, brand required for
  // GREEN). Store context comes from the trip-start pick; at Costco
  // the Kirkland pill leads with an AT-COSTCO kicker.
  const [activeStore] = useActiveStore();
  const [region] = useShoppingRegion();
  const brandPills = useMemo(() => {
    if (!scan.canonicalId || scan.brand) return [];
    const info = getInfo?.(scan.canonicalId) || null;
    return brandsForCanonical(scan.canonicalId, {
      limit: 4,
      info,
      region,
      store: activeStore,
    });
  }, [scan.canonicalId, scan.brand, getInfo, region, activeStore]);
  function pickBrandPill(display) {
    setBrandDraft(display);
    lastWroteBrandRef.current = display;
    tripActions.applyScanPatch(scan.id, { brand: display });
  }

  const stateOptions = (ing?.states || []).map((s) =>
    typeof s === "string" ? { id: s, label: s } : { id: s.id, label: s.label || s.id }
  );
  const cutOptions = (ing?.cuts || []).map((c) =>
    typeof c === "string" ? { id: c, label: c } : { id: c.id, label: c.label || c.id }
  );

  const pickerSpec = useMemo(() => {
    if (!openAxis) return null;
    if (openAxis === "location") {
      return {
        kicker: "LOCATION",
        title: "Where does it live?",
        accent: CHIP_TONES.location.fg,
        options: LOCATION_OPTIONS,
        value: location,
        onPick: (opt) => {
          tripActions.setPlacementOverride(scan.id, {
            location: opt.id,
            tileId: null,  // reset tile when location changes
          });
          // Auto-advance to the tile picker so the location+tile pair
          // feels like a single "where does this go?" wizard.
          setOpenAxis("tile");
        },
      };
    }
    if (openAxis === "tile") {
      return {
        kicker: "STORED IN",
        title: "Which tile?",
        accent: CHIP_TONES.location.fg,
        options: tileOptionsForCanonical(scan.canonicalId, location),
        value: tileId,
        onPick: (opt) => {
          tripActions.setPlacementOverride(scan.id, { tileId: opt.id });
          setOpenAxis(null);
        },
      };
    }
    if (openAxis === "state" && stateOptions.length > 0) {
      return {
        kicker: "STATE",
        title: "What form?",
        accent: CHIP_TONES.state.fg,
        options: stateOptions,
        value: stateValue,
        onPick: (opt) => {
          tripActions.setPlacementOverride(scan.id, { state: opt.id });
          setOpenAxis(null);
        },
      };
    }
    if (openAxis === "cut" && cutOptions.length > 0) {
      return {
        kicker: "CUT",
        title: "Which cut?",
        accent: CHIP_TONES.cut.fg,
        options: cutOptions,
        value: cutValue,
        onPick: (opt) => {
          tripActions.setPlacementOverride(scan.id, { cut: opt.id });
          setOpenAxis(null);
        },
      };
    }
    return null;
  }, [openAxis, location, tileId, stateValue, cutValue, scan.canonicalId, scan.id, stateOptions, cutOptions]);

  const tone = TIER_COLORS[scan.status] || TIER_COLORS.red;
  const display =
    ing?.name ||
    scan.displayName ||
    scan.productName ||
    "(needs name)";

  // Hub-chain breadcrumb — confirms at a glance where the canonical
  // landed: LOCATION › TILE › HUB[ › PARENTHUB]. Walks parentId →
  // parentHub from broad to specific so a sub-hub like pepper_hub
  // reads "Vegetables › Peppers". Standalones (no parentId) just
  // show LOCATION › TILE since there's no hub to surface.
  const breadcrumbParts = useMemo(() => {
    const parts = [];
    if (location) parts.push(LOCATION_OPTIONS.find(l => l.id === location)?.label || location);
    if (tileMeta?.label) parts.push(tileMeta.label);
    if (ing?.parentId) {
      const hubNames = [];
      let cursor = findHub(ing.parentId);
      while (cursor) {
        hubNames.push(cursor.name);
        cursor = cursor.parentHub ? findHub(cursor.parentHub) : null;
      }
      // Reverse so parent hub reads first (broad → specific).
      parts.push(...hubNames.reverse());
    }
    return parts;
  }, [location, tileMeta, ing?.parentId]);

  return (
    <div style={cardStyle(tone)}>
      <div style={iconRowStyle}>
        <button
          onClick={() => setKnownOpen(!knownOpen)}
          style={{ ...iconBtnBase, color: COLOR.muted }}
          aria-label="What we know about this UPC"
          title="What we know about this UPC"
        >
          <BarcodeGlyph />
        </button>
      </div>

      <div style={tierBadgeRow(tone)}>
        <span style={tierDot(tone)} />
        <span>{tone.label}</span>
        {listItem && (
          <>
            <span>·</span>
            <span style={{ color: COLOR.muted, letterSpacing: "0.18em" }}>paired →</span>
            <span style={pairedItemNameStyle}>{listItem.name}</span>
          </>
        )}
        {scan.qty > 1 && (
          <>
            <span>·</span>
            <span>×{scan.qty}</span>
          </>
        )}
      </div>

      {/* Title — paired vs unpaired states are VISUALLY DISTINCT, not
          just colored differently:
            • PAIRED → leaf green, new-kansas italic (the editorial
              item-name treatment used everywhere identity is locked).
              Chain icon. The row reads as a settled identity.
            • UNPAIRED → rose, FONT.forma upright + uppercase mono
              kicker tone, broken-chain icon. The row reads as a
              draft / placeholder, not a confirmed name. The font
              change carries most of the signal even at a glance.
          Tap either state to open the canonical search. */}
      <button
        onClick={() => {
          setOpenAxis("canonical");
          setTimeout(() => canonicalInputRef.current?.focus(), 50);
        }}
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          textAlign: "left",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          alignSelf: "flex-start",
          maxWidth: "100%",
        }}
        aria-label={scan.canonicalId
          ? `Linked to canonical ${ing?.name || scan.canonicalId}. Tap to change.`
          : "No canonical linked. Tap to bind one."}
        title={scan.canonicalId
          ? `Linked to canonical: ${scan.canonicalId}`
          : "No canonical bound — tap to link one"}
      >
        <span style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 20,
          height: 20,
          color: scan.canonicalId ? COLOR.leaf : COLOR.rose,
          flex: "0 0 auto",
        }}>
          {scan.canonicalId ? <LinkGlyph /> : <BrokenLinkGlyph />}
        </span>
        {scan.canonicalId ? (
          <span style={{
            fontFamily: FONT.itemTitle,
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: 22,
            color: COLOR.leaf,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            margin: 0,
          }}>
            {display}
          </span>
        ) : (
          <span style={{
            fontFamily: FONT.forma,
            fontStyle: "normal",
            fontWeight: 600,
            fontSize: 18,
            letterSpacing: "0.02em",
            color: COLOR.rose,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            margin: 0,
            textDecoration: "underline",
            textDecorationStyle: "dashed",
            textUnderlineOffset: "4px",
            textDecorationColor: COLOR.roseDim,
          }}>
            {display}
          </span>
        )}
      </button>

      {/* Hub-chain breadcrumb — confirms at a glance where this scan
          landed in the registry. LOCATION › TILE › HUB[ › PARENTHUB]
          for hub-rooted canonicals; LOCATION › TILE for standalones.
          Read-only — to change placement use the chip below. */}
      {breadcrumbParts.length > 0 && (
        <div
          style={{
            fontFamily: FONT.mono,
            fontSize: 9.5,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: COLOR.muted,
            display: "flex",
            flexWrap: "wrap",
            gap: 5,
            alignItems: "center",
          }}
          aria-label="Resolved hub path"
        >
          {breadcrumbParts.map((part, i) => (
            <span key={`${i}:${part}`} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              {i > 0 && <span style={{ color: COLOR.edge }}>›</span>}
              <span>{part}</span>
            </span>
          ))}
        </div>
      )}

      {/* Brand — inline input. Required for GREEN, so when empty the
          input wears a rose ring + a small "Brand needed" label so
          the user can't miss it. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {!scan.brand && (
          <div style={{
            fontFamily: FONT.mono,
            fontSize: 9,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: COLOR.rose,
          }}>
            Brand needed
          </div>
        )}
        {brandPills.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {brandPills.map((b) => (
              <button
                key={b.slug}
                onClick={() => pickBrandPill(b.display)}
                className="mcm-focusable"
                style={{
                  padding: "6px 12px",
                  borderRadius: 999,
                  border: `1.5px solid ${b.storeMatch ? COLOR.leaf : COLOR.border}`,
                  background: b.storeMatch ? "rgba(47,143,131,0.10)" : "transparent",
                  color: b.storeMatch ? COLOR.leaf : COLOR.ink,
                  fontFamily: FONT.forma,
                  fontSize: 12,
                  fontWeight: b.storeMatch ? 600 : 500,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "baseline",
                  gap: 6,
                }}
              >
                {b.storeMatch && activeStore && (
                  <span style={{
                    fontFamily: FONT.mono,
                    fontSize: 12,
                    letterSpacing: "0.08em",
                    opacity: 0.75,
                  }}>
                    AT {String(activeStore).toUpperCase()}
                  </span>
                )}
                {b.display}
              </button>
            ))}
          </div>
        )}
        <input
          ref={brandInputRef}
          type="text"
          value={brandDraft}
          onChange={(e) => setBrandDraft(e.target.value)}
          onBlur={commitBrand}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commitBrand(); brandInputRef.current?.blur(); }
          }}
          placeholder="Brand (e.g. McCormick)"
          style={{
            ...inputStyle,
            borderColor: scan.brand ? COLOR.edge : COLOR.roseDim,
            borderWidth: scan.brand ? 1 : 1.5,
          }}
        />
      </div>

      {/* Single axis row. Location + tile collapsed into one PLACE
          chip that opens the location→tile wizard. Category isn't
          user-pickable — it's derived from the canonical at commit.
          State + Cut hidden when the canonical has zero options. */}
      <div style={chipRowStyle}>
        <button
          onClick={() => setOpenAxis(openAxis === "size" ? null : "size")}
          style={packageAmount && packageUnit ? chipPaired : chipNeeded}
        >
          {packageAmount && packageUnit
            ? `${packageAmount} ${packageUnit}`
            : "Set size"}
        </button>
        <button
          onClick={() => setWizardOpen(true)}
          style={(location && tileMeta) ? chipPaired : chipNeeded}
        >
          {location && tileMeta
            ? `${location} · ${tileMeta.label}`
            : "Tap to place"}
        </button>
        {stateOptions.length > 0 && (
          <button
            onClick={() => setOpenAxis("state")}
            style={stateValue ? chipPaired : chipNeeded}
          >
            {stateValue ? stateValue : "Set state"}
          </button>
        )}
        {cutOptions.length > 0 && (
          <button
            onClick={() => setOpenAxis("cut")}
            style={cutValue ? chipPaired : chipNeeded}
          >
            {cutValue ? cutValue : "Set cut"}
          </button>
        )}
        {/* Enrichment-readiness chips. Only render once a canonical
            is bound — with no canonical, the missing-data story is
            "needs canonical" and the title's broken-chain glyph
            already signals that. Tapping fires enrich-ingredient
            (paid Haiku, opt-in) and the chip flips when the realtime
            update lands. */}
        {scan.canonicalId && !readiness.hasNutrition && (
          <button
            onClick={enrichOnDemand}
            disabled={enriching}
            style={chipNeeded}
            title="Tap to fetch nutrition for this canonical"
          >
            {enriching ? "Enriching…" : "Nutrition needed"}
          </button>
        )}
        {scan.canonicalId && !readiness.hasDensity && (
          <button
            onClick={enrichOnDemand}
            disabled={enriching}
            style={chipNeeded}
            title="Tap to fetch density / weight for this canonical"
          >
            {enriching ? "Enriching…" : "Density needed"}
          </button>
        )}
      </div>

      {/* Variants — modifier tokens like "spicy", "sharp", "low sodium",
          "smoked". Distinct from axis chips above (canonical / size /
          place / state / cut all describe IDENTITY axes); these capture
          variant info that doesn't change the canonical but should
          travel with the row into the kitchen and be searchable. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ fontFamily: FONT.mono, fontSize: 9, letterSpacing: "0.18em", color: COLOR.muted }}>
          VARIANTS
        </div>
        {Array.isArray(scan.claims) && scan.claims.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {scan.claims.map((claim) => (
              <button
                key={claim}
                onClick={() => {
                  const next = scan.claims.filter((c) => c !== claim);
                  tripActions.applyScanPatch(scan.id, { claims: next });
                }}
                style={{
                  padding: "5px 10px",
                  background: CHIP_TONES.size?.bg || "rgba(58, 140, 90, 0.10)",
                  border: `1px solid ${CHIP_TONES.size?.fg || "rgba(58, 140, 90, 0.32)"}`,
                  borderRadius: 999,
                  fontFamily: FONT.detail, fontStyle: "italic", fontSize: 12,
                  color: COLOR.ink,
                  cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 5,
                }}
                aria-label={`Remove variant ${claim}`}
              >
                {claim}
                <span style={{ opacity: 0.5, fontSize: 13, lineHeight: 1 }}>×</span>
              </button>
            ))}
          </div>
        )}
        <input
          type="text"
          placeholder="add variant — spicy, sharp, organic, low sodium…"
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            const v = String(e.currentTarget.value || "").trim().toLowerCase();
            if (!v) return;
            const existing = Array.isArray(scan.claims) ? scan.claims : [];
            if (existing.includes(v)) {
              e.currentTarget.value = "";
              return;
            }
            tripActions.applyScanPatch(scan.id, { claims: [...existing, v] });
            e.currentTarget.value = "";
          }}
          style={{
            ...inputStyle,
            fontStyle: "italic",
            fontSize: 14,
          }}
        />
      </div>

      {/* Inline canonical search (opens when title or "+ canonical" tapped) */}
      {openAxis === "canonical" && (
        <div style={{ position: "relative" }}>
          <input
            ref={canonicalInputRef}
            type="text"
            value={canonicalSearch}
            onChange={(e) => setCanonicalSearch(e.target.value)}
            placeholder="Search canonicals…"
            style={{ ...inputStyle, borderColor: CHIP_TONES.canonical.fg }}
          />
          <RisingResults
            inputRef={canonicalInputRef}
            open={canonicalSearchItems.length > 0}
            onClose={() => setOpenAxis(null)}
            items={canonicalSearchItems}
            onPick={pickCanonical}
            renderItem={(it) => (
              <div style={{ padding: "8px 10px", fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 14, color: COLOR.ink }}>
                {it.label}
              </div>
            )}
          />
          {/* Create-new affordance — fires when search has at least 3
              chars and zero matches in the registry. Mints a pending
              canonical via createPendingCanonicalFromScan so the user
              isn't dead-ended when their item isn't bundled yet. */}
          {canonicalSearch.trim().length >= 3 &&
            canonicalSearchItems.length === 0 &&
            userId && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); createCanonicalFromSearch(); }}
              disabled={creatingCanonical}
              style={{
                marginTop: 6,
                width: "100%",
                padding: "10px 12px",
                fontFamily: FONT.mono,
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: COLOR.leaf,
                background: COLOR.leafDeep,
                border: `1px dashed ${COLOR.leafDim}`,
                borderRadius: RADIUS.sm,
                cursor: creatingCanonical ? "wait" : "pointer",
                textAlign: "left",
                opacity: creatingCanonical ? 0.6 : 1,
              }}
            >
              {creatingCanonical
                ? `Creating "${canonicalSearch.trim()}"…`
                : `+ Create canonical: "${canonicalSearch.trim()}"`}
            </button>
          )}
        </div>
      )}

      {/* Inline package-size editor */}
      {openAxis === "size" && (
        <div style={{
          padding: 10,
          background: COLOR.night,
          border: `1px solid ${COLOR.edge}`,
          borderRadius: RADIUS.md,
        }}>
          <PackageSizeStep
            canonicalId={scan.canonicalId}
            brand={scan.brand}
            value={packageAmount && packageUnit ? { amount: packageAmount, unit: packageUnit } : null}
            onChange={({ amount, unit }) => {
              tripActions.setPlacementOverride(scan.id, {
                packageAmount: amount,
                packageUnit: unit,
              });
            }}
            onCommit={() => setOpenAxis(null)}
          />
        </div>
      )}

      {/* What we know — barcode-icon-toggled panel. Hidden by default. */}
      {knownOpen && (
        <div style={knownPanelStyle}>
          <div><strong>UPC:</strong> {scan.barcodeUpc || "—"}</div>
          {off?.productName && <div><strong>OFF name:</strong> {off.productName}</div>}
          {off?.brand && <div><strong>OFF brand:</strong> {off.brand}</div>}
          {off?.quantity && <div><strong>OFF size:</strong> {off.quantity}</div>}
          {off?.categoryHints?.length > 0 && (
            <div><strong>OFF categories:</strong> {off.categoryHints.slice(0, 6).join(", ")}</div>
          )}
          {!off && <div>No external data — this UPC was named manually.</div>}
        </div>
      )}

      {pickerSpec && (
        <ChipModal
          open={!!pickerSpec}
          onClose={() => setOpenAxis(null)}
          kicker={pickerSpec.kicker}
          title={pickerSpec.title}
          accent={pickerSpec.accent}
          options={pickerSpec.options}
          value={pickerSpec.value}
          onPick={pickerSpec.onPick}
        />
      )}

      {wizardOpen && (
        <AddItemSheet
          seed={{
            mode: "wizard",
            canonicalId: scan.canonicalId || null,
            name: ing?.name || scan.displayName || null,
            barcodeUpc: scan.barcodeUpc || null,
            // Hand the already-resolved location + tile to the wizard
            // so it doesn't ask the user to re-pick. AddItemSheet
            // falls back to getCanonicalHome when these are absent.
            location: location || null,
            tileId: tileId || null,
          }}
          title={`Placing ${ing?.name || scan.displayName || scan.productName || "item"}`}
          userId={null}
          isAdmin={false}
          onClose={() => setWizardOpen(false)}
          onSubmit={async (payload) => {
            // The wizard's buildPantryRow returns CAMEL-case keys
            // (canonicalId / tileId / typeId / barcodeUpc / etc.).
            // setPlacementOverride merges the whole payload into the
            // jsonb override and the chip render reads back from
            // overrides.tileId / overrides.location.
            console.info("[ResolveCard.wizard.onSubmit] payload:", {
              canonicalId: payload.canonicalId,
              tileId: payload.tileId,
              location: payload.location,
              brand: payload.brand,
              max: payload.max,
              unit: payload.unit,
              state: payload.state,
              cut: payload.cut,
            });
            tripActions.setPlacementOverride(scan.id, payload);
            // Brand + canonical also need to land on trip_scans (real
            // DB columns). DB columns are snake_case, payload is
            // camelCase — translate at the boundary. Earlier shipped
            // version checked `payload.canonical_id` which never
            // existed, so canonical changes from the wizard never
            // propagated back to the scan row.
            const scanPatch = {};
            if (payload.brand && payload.brand !== scan.brand) {
              scanPatch.brand = payload.brand;
            }
            if (payload.canonicalId && payload.canonicalId !== scan.canonicalId) {
              scanPatch.canonical_id = payload.canonicalId;
              scanPatch.product_name = payload.name || null;
            }
            if (Object.keys(scanPatch).length > 0) {
              console.info("[ResolveCard.wizard.onSubmit] scanPatch:", scanPatch);
              tripActions.applyScanPatch(scan.id, scanPatch);
            }
            setWizardOpen(false);
            return { ok: true };
          }}
        />
      )}

    </div>
  );
}

const EMPTY_OVERRIDES = {};

function defaultLocationForCategory(category) {
  if (!category) return null;
  if (["dairy", "meat", "seafood", "produce"].includes(category)) return "fridge";
  return "pantry";
}

function parsedPkg(off) {
  if (!off?.quantity) return null;
  // light parse — full canonicalResolver.parsePackageSize handles
  // multipack / dual-unit cases. For visual display we just need a
  // first-pass split. Fall back to canonicalResolver for completeness
  // when implemented.
  const m = String(off.quantity).match(/(\d+(?:\.\d+)?)\s*([a-zA-Z]+)/);
  if (!m) return null;
  return { amount: Number(m[1]), unit: m[2].toLowerCase() };
}

function BarcodeGlyph() {
  // Minimalist barcode icon — vertical bars. Pure SVG, no emoji.
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <g fill="currentColor">
        <rect x="1" y="2" width="1" height="10" />
        <rect x="3" y="2" width="2" height="10" />
        <rect x="6" y="2" width="1" height="10" />
        <rect x="8" y="2" width="2" height="10" />
        <rect x="11" y="2" width="1" height="10" />
        <rect x="13" y="2" width="0.5" height="10" />
      </g>
    </svg>
  );
}

// Two-link chain — signals "this scan is connected to a canonical
// in our registry." Drawn at 16px to sit next to the 22px title
// without dominating it. Stroke uses currentColor so the parent's
// leaf/rose color flows through.
function LinkGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6.5 9.5 L9.5 6.5" />
      <path d="M5.5 4.5 a2.5 2.5 0 0 1 3.5 0 l1.5 1.5 a2.5 2.5 0 0 1 0 3.5" />
      <path d="M10.5 11.5 a2.5 2.5 0 0 1 -3.5 0 l-1.5 -1.5 a2.5 2.5 0 0 1 0 -3.5" />
    </svg>
  );
}

// Broken chain — same silhouette as LinkGlyph but the two halves
// have separated and a small gap line slashes between them. Signals
// "no canonical linked yet."
function BrokenLinkGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5.5 4.5 a2.5 2.5 0 0 1 3.5 0 l0.7 0.7" />
      <path d="M10.5 11.5 a2.5 2.5 0 0 1 -3.5 0 l-0.7 -0.7" />
      <path d="M3.5 12.5 L12.5 3.5" />
    </svg>
  );
}

